import { BootstrapConfig, Geo } from 'core/type/BootstrapConfig.types';
import { Category, SearchResult } from 'core/type/SuggestionResult.types';
import { getPathName, getPathWithCultureCode, locationSearch, stripCultureCode, withQueryString } from 'core/service/Url/Url';
import { host, protocol } from 'core-framework/window/location';

import { AppConfiguration } from 'core/configuration/AppConfiguration';
import { ContentMetaInfoPax } from 'core/type/CuscoResponse.types';
import { ExperimentId } from 'core/type/ExperimentId';
import { LocalStorageKey } from 'core-framework/constant/StorageKeys';
import { NavigationState } from 'store/slice';
import { PaxTypeCodeToIdMap } from 'core/utils/getPax';
import { SearchQueryKey } from 'core-framework/constant/SearchQueryKey';
import { TextSearchType } from 'core/type/TextSearch.types';
import { getBootstrapConfig } from 'core/window/windowService';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import { isB } from 'core/window/bootstrapConfig';
import { removeItemFromLocalStorage } from 'core-framework/window/storage';

export const getCid = (config: BootstrapConfig): string => {
    return (config.pageConfig?.cid || -1).toString();
};

const HomePageIgnoreQueryParams = ['activityId', 'activityToken', 'cityId', 'countryId', ...Object.values(SearchQueryKey)];

const ActivityInternalPaths = ['/search', '/detail', '/activities'];
const ActivityBookingFormPaths = ['/activities/book', '/activities/payment', '/activities/thankyou'];

// Make SearchPageFilterAndSortParams  as constant when Integrate ACT_4518
const getSearchPageFilterAndSortParams = (): string[] => {
    return ['page', 'sort', 'category', 'priceFrom', 'priceTo', 'duration', 'rating', 'keyword', 'subcategory', 'bookingFeature'];
};

const getSearchPageIgnoreParamsForKeywordSearch = (): string[] => {
    const SearchPageIgnoreQueryParams = ['activityId', 'cityId', 'selectedActivity', ...getSearchPageFilterAndSortParams()];
    return SearchPageIgnoreQueryParams.filter((param) => param !== 'keyword');
};

// Make SearchPageIgnoreQueryParams as constant when Integrate ACT_4518
const getSearchPageIgnoreParams = (config: BootstrapConfig): string[] => {
    const SearchPageIgnoreQueryParams = ['activityId', 'travelDate', 'enrichedOfferId', 'offerOptionId', 'pax', ...getSearchPageFilterAndSortParams()];
    if (isB(ExperimentId.ACT_4518, config.experiments)) return SearchPageIgnoreQueryParams;
    return SearchPageIgnoreQueryParams.filter((param) => param !== 'keyword');
};

// Make DetailsPageIgnoreQueryParams as constant when Integrate ACT_4518
const getDetailsIgnoreParams = (config: BootstrapConfig): string[] => {
    const DetailsPageIgnoreQueryParams = ['selectedActivity', ...getSearchPageFilterAndSortParams()];
    if (isB(ExperimentId.ACT_4518, config.experiments)) return DetailsPageIgnoreQueryParams;
    return DetailsPageIgnoreQueryParams.filter((param) => param !== 'keyword');
};

export const isInternalPage = (url: string): boolean => {
    if (!url || url === '/') return true; // Home page

    let path = stripCultureCode(url);
    path = path.split('?')[0];

    if (!path || path === '/') return true; // Home page
    for (const bfPath of ActivityBookingFormPaths) {
        if (path.startsWith(bfPath)) {
            return false;
        }
    }

    for (const internalPath of ActivityInternalPaths) {
        if (path.startsWith(internalPath)) {
            return true;
        }
    }
    return false;
};

const generateHomeUrl = (configParams?: Partial<BootstrapConfig>, replaceActivitiesPath = false): string => {
    const config = { ...getBootstrapConfig(), ...configParams };
    const cid = getCid(config);
    const rootPath = config.isWebView && !config.rootUrl ? '/activities' : `${config.rootUrl || ''}/`;
    const path = withQueryString(rootPath, cid, undefined, HomePageIgnoreQueryParams);
    const pathWithCultureCode = getPathWithCultureCode(path, config.language.cultureCode, cid, HomePageIgnoreQueryParams, true, config);

    if (replaceActivitiesPath && !(config.isMockData || config.isWebView)) {
        return pathWithCultureCode.replace('/activities', '/activity');
    }
    return pathWithCultureCode;
};

export const getHomeUrl = (configParams?: Partial<BootstrapConfig>): string => {
    return generateHomeUrl(configParams, true);
};

export const getActivitiesHomeUrl = (configParams?: Partial<BootstrapConfig>): string => {
    return generateHomeUrl(configParams);
};

/**
 * Do not use any white space characters in URL.
 * The language change feature will break if the 'HttpRequest.Path' is not same as 'HttpRequest.Referer'
 * Target code: Agoda.LCD.NetCore.Language.CookieSetResolution
 * @param name
 */
const getSanitizeName = (name?: string) => {
    if (!name || name === '') {
        return '';
    }
    return name.split(' ').join('_');
};

export const getDeSanitizeName = (name?: string) => {
    if (!name || name === '') {
        return '';
    }
    return name.split('_').join(' ');
};

const joinUrlPaths = (...paths: string[]) => {
    return paths
        .map(getSanitizeName)
        .filter((p) => p)
        .join('/')
        .toLowerCase();
};

const generateQueryParams = (params: Record<string, string>) => {
    const queryParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
        if (value) {
            queryParams[key] = value;
        }
    });
    return queryParams;
};

export const getAreaUrl = (areaId: number, config: BootstrapConfig): string => {
    const geo = config.geo || ({} as Geo);
    if (geo && !config.isMockData) {
        return joinUrlPaths('/search/area', geo.countryName, geo.cityName, geo.areaName);
    }
    return `/search?areaId=${areaId}`;
};

export const getCityUrl = (cityId: number, config: BootstrapConfig): string => {
    const geo = config.geo || ({} as Geo);
    if (geo && !config.isMockData) {
        return joinUrlPaths('/search/city', geo.countryName, geo.cityName);
    }
    return `/search?cityId=${cityId}`;
};

export const getCountryUrl = (countryId: number, config: BootstrapConfig): string => {
    const geo = config.geo || ({} as Geo);
    if (geo && !config.isMockData) {
        return joinUrlPaths('/search/country', geo.countryName);
    }
    return `/search?countryId=${countryId}`;
};

export const getRegionUrl = (regionId: number, config: BootstrapConfig): string => {
    const geo = config.geo || ({} as Geo);
    if (geo && !config.isMockData) {
        return joinUrlPaths('/search/region', geo.countryName, geo.stateName);
    }
    return `/search?regionId=${regionId}`;
};

export const getSearchPageUrl = (result: SearchResult, travelDate?: Date, category?: Category, config = getBootstrapConfig()): string => {
    removeItemFromLocalStorage(LocalStorageKey.ACTIVITY_SCROLL_POSITION_KEY);
    const rootUrl = config.rootUrl || '';
    const cid = getCid(config);
    const searchPath = result.url.replace(/\/+/g, '/');

    const travelDateString = travelDate ? getDateInYmdFormat(travelDate) : undefined;

    const params = {} as Record<string, string>;
    if (travelDateString) {
        params[SearchQueryKey.TravelDate] = travelDateString;
    }
    const ignoreParams = [...getSearchPageIgnoreParams(config)];

    if (result.type !== TextSearchType.Activity) {
        ignoreParams.push(SearchQueryKey.SelectedActivity);
    }

    if (result.isCityPageEligible && searchPath.includes('/city')) {
        ignoreParams.push(SearchQueryKey.CityId);
    }
    if (category) {
        params[SearchQueryKey.Category] = `${category.id}`;
        ignoreParams.splice(ignoreParams.indexOf('category'), 1);
    }
    const queryParams = generateQueryParams(params);
    const path = withQueryString(`${rootUrl}${searchPath}`, cid, queryParams);
    return getPathWithCultureCode(path, config.language.cultureCode, cid, ignoreParams, true, config);
};

export const appendQueryStringToCityUrl = (path: string, config = getBootstrapConfig()): string => {
    const cid = getCid(config);
    const ignoreParams = [...getSearchPageIgnoreParams(config), SearchQueryKey.SelectedActivity];

    if (path.includes('/city')) {
        ignoreParams.push(SearchQueryKey.CityId);
    }

    const pathWithQueryString = withQueryString(path, cid);

    return getPathWithCultureCode(pathWithQueryString, config.language.cultureCode, cid, ignoreParams, true, config);
};

export const getCityPageFromGeo = (geo: Geo, isIgnoreKeyword = false, config = getBootstrapConfig()): string => {
    removeItemFromLocalStorage(LocalStorageKey.ACTIVITY_SCROLL_POSITION_KEY);

    const rootUrl = config.rootUrl || '';
    const cid = getCid(config);
    const searchPath = `/city/${geo.countryName.toLowerCase()}/${geo.cityName.toLowerCase()}`;

    const params = {} as Record<string, string>;
    const queryParams = generateQueryParams(params);
    const path = withQueryString(`${rootUrl}${searchPath}`, cid, queryParams);
    return getPathWithCultureCode(path, config.language.cultureCode, cid, isIgnoreKeyword ? [SearchQueryKey.Keyword] : undefined, true, config);
};

export const getSearchPageFromCityIdUrl = (cityId: number, config = getBootstrapConfig()): string => {
    removeItemFromLocalStorage(LocalStorageKey.ACTIVITY_SCROLL_POSITION_KEY);

    const rootUrl = config.rootUrl || '';
    const cid = getCid(config);
    const searchPath = `/search?cityId=${cityId}`;

    const params = {} as Record<string, string>;
    const queryParams = generateQueryParams(params);
    const path = withQueryString(`${rootUrl}${searchPath}`, cid, queryParams);
    return getPathWithCultureCode(path, config.language.cultureCode, cid, undefined, true, config);
};

export const getSearchPageFromCityIdUrlWithoutResetScrollPosition = (cityId: number, config = getBootstrapConfig()): string => {
    const rootUrl = config.rootUrl || '';
    const cid = getCid(config);
    const searchPath = `/search?cityId=${cityId}`;

    const params = {} as Record<string, string>;
    const queryParams = generateQueryParams(params);
    const path = withQueryString(`${rootUrl}${searchPath}`, cid, queryParams);
    return getPathWithCultureCode(path, config.language.cultureCode, cid, undefined, true, config);
};

export const getSearchPageFromCityIdAndCategoryIdUrlWithoutResetScrollPosition = (cityId: number, categoryId: number, config = getBootstrapConfig()): string => {
    const rootUrl = config.rootUrl || '';
    const cid = getCid(config);
    const searchPath = `/search?cityId=${cityId}&category=${categoryId}`;
    const params = {} as Record<string, string>;
    const queryParams = generateQueryParams(params);
    const path = withQueryString(`${rootUrl}${searchPath}`, cid, queryParams);
    return getPathWithCultureCode(path, config.language.cultureCode, cid, undefined, true, config);
};

export const getSearchPageWithSubcategoryIdUrl = (subcategoryQueryKey: string, config = getBootstrapConfig()): string => {
    const rootUrl = config.rootUrl || '';
    const cityId = config.geo?.cityId;
    const cid = getCid(config);
    const searchPath = `/search?cityId=${cityId}&subcategory=${subcategoryQueryKey}`;
    const ignoreParams = [...getSearchPageIgnoreParams(config)].filter((param) => param !== 'subcategory');

    const params = {} as Record<string, string>;
    const queryParams = generateQueryParams(params);
    const path = withQueryString(`${rootUrl}${searchPath}`, cid, queryParams);
    return getPathWithCultureCode(path, config.language.cultureCode, cid, ignoreParams, true, config);
};

export const getSearchPageFromCityIdAndActivityIdUrl = (cityId: number, activityId: number, keyword = '', config = getBootstrapConfig()): string => {
    removeItemFromLocalStorage(LocalStorageKey.ACTIVITY_SCROLL_POSITION_KEY);

    const rootUrl = config.rootUrl || '';
    const cid = getCid(config);
    const searchPath = `/search?cityId=${cityId}&selectedActivity=${activityId}`;

    const params = {} as Record<string, string>;
    const queryParams = generateQueryParams(params);
    const path = withQueryString(`${rootUrl}${searchPath}`, cid, queryParams);
    return getPathWithCultureCode(path, config.language.cultureCode, cid, undefined, true, config);
};

export const getSearchPageFromCityIdAndKeyword = (cityId: number, keyword: string, config = getBootstrapConfig()): string => {
    removeItemFromLocalStorage(LocalStorageKey.ACTIVITY_SCROLL_POSITION_KEY);

    const rootUrl = config.rootUrl || '';
    const cid = getCid(config);
    const searchPath = `/search?cityId=${cityId}&keyword=${encodeURI(keyword)}`;

    const params = {} as Record<string, string>;
    const queryParams = generateQueryParams(params);
    const path = withQueryString(`${rootUrl}${searchPath}`, cid, queryParams);
    return getPathWithCultureCode(path, config.language.cultureCode, cid, undefined, true, config);
};

export const getSearchPageUrlFromGlobalKeyword = (keyword: string, config = getBootstrapConfig()): string => {
    removeItemFromLocalStorage(LocalStorageKey.ACTIVITY_SCROLL_POSITION_KEY);

    const rootUrl = config.rootUrl || '';
    const cid = getCid(config);
    const searchPath = `/search?keyword=${encodeURI(keyword)}`;
    const params = {} as Record<string, string>;
    const queryParams = generateQueryParams(params);
    const ignoreParams = getSearchPageIgnoreParamsForKeywordSearch();
    const path = withQueryString(`${rootUrl}${searchPath}`, cid, queryParams, ignoreParams);
    return getPathWithCultureCode(path, config.language.cultureCode, cid, ignoreParams, true, config);
};

export const getPaxQueryParams = (pax: ContentMetaInfoPax[]): string => {
    return pax.map((p) => `${PaxTypeCodeToIdMap[p.paxType]}_${p.quantity}`).join(',');
};

export const getDetailUrl = (
    activityId: number,
    cultureCode: string,
    cityId?: number,
    travelDate?: Date,
    endDate?: Date,
    config = getBootstrapConfig(),
    enrichedOfferId?: number,
    offerOptionId?: number,
    pax?: ContentMetaInfoPax[],
): string => {
    const cid = getCid(config);
    const inputRoute = `${config.rootUrl || ''}/detail?activityId=${activityId}`;
    const cityIdParam = cityId ?? config.geo?.cityId;
    const travelDateString = travelDate && getDateInYmdFormat(travelDate);
    const endDateString = endDate && getDateInYmdFormat(endDate);
    const paxDetail = pax && getPaxQueryParams(pax);
    const ignoreParams = [...getDetailsIgnoreParams(config)];

    const params = {} as Record<string, string>;
    if (travelDateString) {
        params[SearchQueryKey.TravelDate] = travelDateString;
    }
    if (endDateString) {
        params[SearchQueryKey.EndDate] = endDateString;
    }
    if (cityIdParam) {
        params[SearchQueryKey.CityId] = cityIdParam.toString();
    }
    if (enrichedOfferId && enrichedOfferId > 0) {
        params[SearchQueryKey.EnrichedOfferId] = enrichedOfferId.toString();
    } else {
        ignoreParams.push(SearchQueryKey.EnrichedOfferId);
    }
    if (offerOptionId !== undefined && offerOptionId >= 0) {
        params[SearchQueryKey.OfferOptionId] = offerOptionId.toString();
    } else {
        ignoreParams.push(SearchQueryKey.OfferOptionId);
    }
    if (paxDetail) {
        params[SearchQueryKey.Pax] = paxDetail.toString();
    } else {
        ignoreParams.push(SearchQueryKey.Pax);
    }

    const queryParams = generateQueryParams(params);

    const path = withQueryString(inputRoute, cid, queryParams, ignoreParams);
    return getPathWithCultureCode(path, cultureCode, cid, ignoreParams, true, config);
};

export const getLoginReturnUrl = (state: NavigationState, config: BootstrapConfig): string => {
    const cid = getCid(config);
    const returnUrl = `${stripCultureCode(getPathName(state))}${locationSearch(state)}`;
    const withQS = encodeURIComponent(withQueryString(returnUrl, cid, undefined));
    return withQS;
};

export const getLoginUrl = (isDesktop: boolean, state: NavigationState, cultureCode: string, config = getBootstrapConfig()): string => {
    const loginReturnUrl = getLoginReturnUrl(state, config);
    const encodedUrl = isDesktop ? `?returnurl=${loginReturnUrl}` : `?targeturl=${loginReturnUrl}`;
    const targetUrl = isDesktop ? '/account/signin.html' : '/signin';
    const path = getPathWithCultureCode(targetUrl, cultureCode, '-1', [], false, config);
    return path + encodedUrl;
};

const buildBookingFormUrl = (config: BootstrapConfig) => {
    if (AppConfiguration.isPreLive()) {
        return host();
    }
    // MSPA local is running on localhost:8080
    return AppConfiguration.isDev() ? 'localhost:8080' : config.whiteLabelConfig.bookingFormUrl;
};

export const getPathPrefix = (pathname: string, isWebView: boolean) => {
    if (isWebView && !pathname.includes('/app')) {
        return `/app${pathname}`;
    }
    return pathname;
};

export const getBookingFormUrl = (activityToken: string, activityOfferToken = '', isCartMigrationEnabled: boolean, config = getBootstrapConfig()): string => {
    const cid = getCid(config);
    const bookingFormUrl = buildBookingFormUrl(config);
    const isWebView = config.isWebView;
    const activitiesBookingFormPath = `${getPathPrefix('/activities', isWebView)}/book?activityToken=${activityToken}&activityOfferToken=${activityOfferToken}`;
    const cartBookingFormPath = `${getPathPrefix('/packages', isWebView)}/book?pt=${activityToken}&ptyp=11`;
    const path = isCartMigrationEnabled ? cartBookingFormPath : activitiesBookingFormPath;
    const ignoreQueryStrings = isCartMigrationEnabled ? ['activityToken'] : [];
    const pathWithQuery = getPathWithCultureCode(path, config.language.cultureCode, cid, [], true, config);
    const bookingFormUrlWithQuery = isCartMigrationEnabled ? `${protocol()}//${bookingFormUrl}${pathWithQuery}` : `//${bookingFormUrl}${pathWithQuery}`;
    return withQueryString(bookingFormUrlWithQuery, cid, undefined, ignoreQueryStrings);
};

export const getImageUrl = (image: string, config = getBootstrapConfig()): string => {
    const DefaultWhiteLabelId = 1;
    const targetWhiteLabelId = config.behavior.common.activitiesWhitelabelIdMapping?.targetWhitelabelId || config.pageConfig.whitelabelid || DefaultWhiteLabelId;

    return `${config.cdn}/cdn-activities/images/${targetWhiteLabelId}/${image}`;
};

export const getPropertyHighlightsImageUrl = (image: string, config = getBootstrapConfig()): string => {
    return `${config.cdn}/images/property/highlights/${image}`;
};
